import { useEffect, useRef, useMemo, useState } from 'react';
import type { RentalProperty } from '@/types';
import { formatNumber } from '@/utils';

function fmtRent(str: string) {
  const n = parseFloat(str);
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(0)}tr/th`;
  return formatNumber(n) + '/th';
}
function getPrimaryImg(images: any[]) {
  if (!images?.length) return null;
  return (images.find((i) => i.isPrimary) ?? images[0])?.image?.publicUrl ?? null;
}

export default function PropertyMap({ items, selected, onSelect, locationQuery }: { items: RentalProperty[]; selected: string | null; onSelect: (id: string) => void; locationQuery?: string }) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const clusterGroupRef = useRef<any>(null);
  const polygonRef = useRef<any>(null);
  const routeLayerRef = useRef<any>(null);
  const routeLabelsRef = useRef<any[]>([]);

  const [showRoute, setShowRoute] = useState(false);
  const [routeLoading, setRouteLoading] = useState(false);

  useEffect(() => {
    // Load CSS
    const loadCss = (id: string, href: string) => {
      if (!document.getElementById(id)) {
        const link = document.createElement('link');
        link.id = id; link.rel = 'stylesheet'; link.href = href;
        document.head.appendChild(link);
      }
    };
    loadCss('leaflet-css', 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css');
    loadCss('markercluster-css', 'https://unpkg.com/leaflet.markercluster@1.4.1/dist/MarkerCluster.css');
    loadCss('markercluster-default-css', 'https://unpkg.com/leaflet.markercluster@1.4.1/dist/MarkerCluster.Default.css');

    const loadScript = (src: string, isLoaded: () => boolean) => {
      return new Promise((resolve) => {
        if (isLoaded()) return resolve(true);
        let script = document.querySelector(`script[src="${src}"]`) as HTMLScriptElement;
        if (script) {
          const interval = setInterval(() => {
            if (isLoaded()) {
              clearInterval(interval);
              resolve(true);
            }
          }, 50);
          script.addEventListener('load', () => {
            clearInterval(interval);
            resolve(true);
          });
          return;
        }
        script = document.createElement('script');
        script.src = src;
        script.onload = () => resolve(true);
        document.head.appendChild(script);
      });
    };

    const initMap = async () => {
      await loadScript('https://unpkg.com/leaflet@1.9.4/dist/leaflet.js', () => !!(window as any).L);
      await loadScript('https://unpkg.com/leaflet.markercluster@1.4.1/dist/leaflet.markercluster.js', () => !!(window as any).L?.markerClusterGroup);

      const L = (window as any).L;
      if (!L || !mapRef.current || mapInstanceRef.current) return;
      const map = L.map(mapRef.current, { zoomControl: true }).setView([10.7769, 106.7009], 12);

      // Use Google Maps Standard (Roadmap) Tile Layer
      L.tileLayer('https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}', {
        attribution: '© Google Maps',
        maxZoom: 20,
      }).addTo(map);

      mapInstanceRef.current = map;
      renderMarkers(L, map);

      setTimeout(() => {
        if (mapInstanceRef.current) {
          mapInstanceRef.current.invalidateSize();
        }
      }, 200);
    };

    initMap();

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!mapRef.current) return;
    const observer = new ResizeObserver(() => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.invalidateSize();
      }
    });
    observer.observe(mapRef.current);
    return () => observer.disconnect();
  }, []);

  const jitteredItems = useMemo(() => {
    return items.map((item) => {
      let lat = parseFloat(item.latitude);
      let lng = parseFloat(item.longitude);

      // Nếu API không trả về toạ độ, gán toạ độ mặc định theo tỉnh/thành
      if (isNaN(lat) || isNaN(lng)) {
        const provinceName = item.province?.name?.toLowerCase() || '';
        if (provinceName.includes('hà nội')) {
          lat = 21.0285;
          lng = 105.8542;
        } else if (provinceName.includes('đà nẵng')) {
          lat = 16.0544;
          lng = 108.2022;
        } else {
          // Mặc định là TP.HCM
          lat = 10.7769;
          lng = 106.7009;
        }
      }

      // Thêm sai số ngẫu nhiên để các điểm không đè lên nhau hoàn toàn
      const latOffset = (Math.random() - 0.5) * 0.02;
      const lngOffset = (Math.random() - 0.5) * 0.02;

      return {
        ...item,
        renderLat: lat + latOffset,
        renderLng: lng + lngOffset,
      };
    });
  }, [items]);

  const renderMarkers = (L: any, map: any) => {
    if (clusterGroupRef.current) {
      map.removeLayer(clusterGroupRef.current);
    }

    const clusterGroup = L.markerClusterGroup({
      showCoverageOnHover: false,
      iconCreateFunction: function (cluster: any) {
        const count = cluster.getChildCount();
        return L.divIcon({
          html: `<div style="
            background: #f97316;
            color: #fff;
            border: 2px solid #fff;
            border-radius: 50%;
            width: 36px; height: 36px;
            display: flex; align-items: center; justify-content: center;
            font-size: 14px; font-weight: 700;
            box-shadow: 0 2px 6px rgba(0,0,0,0.3);
          ">${count}</div>`,
          className: '',
          iconSize: [36, 36],
        });
      }
    });

    jitteredItems.forEach((item) => {
      const lat = item.renderLat;
      const lng = item.renderLng;

      if (!lat || !lng) return;
      const isSelected = item.id === selected;

      const priceStr = fmtRent(item.monthlyRent);

      // Marker showing price pill with pointer
      const icon = L.divIcon({
        className: '',
        html: `<div style="
          background: #f97316;
          color: #fff;
          border: ${isSelected ? '2px solid #000' : 'none'};
          border-radius: 20px;
          padding: 4px 10px;
          display: flex; align-items: center; justify-content: center;
          font-size: 13px; font-weight: 700;
          box-shadow: 0 2px 6px rgba(0,0,0,0.3);
          white-space: nowrap;
          width: max-content;
          position: relative;
          z-index: ${isSelected ? 1000 : 1};
          transition: all 0.2s ease;
          transform: ${isSelected ? 'scale(1.1)' : 'scale(1)'};
        ">
          ${priceStr}
          <div style="
            position: absolute;
            bottom: -5px;
            left: 50%;
            transform: translateX(-50%);
            border-width: 6px 6px 0;
            border-style: solid;
            border-color: ${isSelected ? '#000' : '#f97316'} transparent transparent transparent;
          "></div>
          ${isSelected ? `<div style="
            position: absolute;
            bottom: -3px;
            left: 50%;
            transform: translateX(-50%);
            border-width: 4px 4px 0;
            border-style: solid;
            border-color: #f97316 transparent transparent transparent;
          "></div>` : ''}
        </div>`,
        iconSize: null as any,
        iconAnchor: [20, 20],
      });

      const pricePerM2 = item.area && parseFloat(item.area) > 0 ? (parseFloat(item.monthlyRent) / parseFloat(item.area) / 1000000).toFixed(1) + ' tr/m²' : '';

      const marker = L.marker([lat, lng], { icon })
        .bindPopup(
          `
          <a href="/cho-thue/${item.id}" style="display:block; width: 220px; text-decoration: none; color: inherit; font-family: inherit;">
            <div style="position: relative; border-radius: 8px; overflow: hidden; height: 140px; margin-bottom: 8px; background: #eee;">
              <img src="${getPrimaryImg(item.images) ?? ''}" style="width: 100%; height: 100%; object-fit: cover;" />
              <span style="position: absolute; top: 8px; left: 8px; background: #f97316; color: #fff; padding: 2px 6px; border-radius: 4px; font-size: 10px; font-weight: bold;">
                ${item.isAvailable ? 'Còn trống' : 'Đã thuê'}
              </span>
            </div>
            <div style="display:flex; align-items: center; gap: 4px; margin-bottom: 4px;">
               <span style="background: #f3f4f6; color: #4b5563; padding: 2px 6px; border-radius: 4px; font-size: 10px; font-weight: 600; text-transform: uppercase;">${item.propertyType || 'Mặt bằng'}</span>
               <span style="background: #ecfdf5; color: #059669; padding: 2px 6px; border-radius: 4px; font-size: 10px; font-weight: 600;">✓ Xác thực</span>
            </div>
            <p style="font-weight:700; font-size:13px; margin:0 0 4px; line-height: 1.4; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; color: #111827;">
              ${item.title}
            </p>
            <div style="display: flex; align-items: baseline; justify-content: space-between; margin-bottom: 4px;">
              <p style="color:#f97316; font-weight:800; font-size:16px; margin:0;">${fmtRent(item.monthlyRent)}</p>
              <p style="color:#6b7280; font-size: 11px; margin: 0;">${pricePerM2}</p>
            </div>
            <p style="font-size:11px; color:#6b7280; margin:0 0 8px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
              ${item.district?.name}, ${item.province?.name}
            </p>
            <div style="border-top: 1px solid #f3f4f6; padding-top: 8px; display: flex; gap: 12px; font-size: 11px; color: #6b7280; align-items: center;">
              ${item.area ? `<span style="display: flex; align-items: center; gap: 4px;">📐 ${item.area}m²</span>` : ''}
              ${item.floorLevel ? `<span style="display: flex; align-items: center; gap: 4px;">🏢 Tầng ${item.floorLevel}</span>` : ''}
            </div>
          </a>
        `,
          { maxWidth: 220, offset: [0, -10] }
        )
        .on('click', () => onSelect(item.id));

      clusterGroup.addLayer(marker);
    });

    map.addLayer(clusterGroup);
    clusterGroupRef.current = clusterGroup;
  };

  useEffect(() => {
    const L = (window as any).L;
    if (L && mapInstanceRef.current && L.markerClusterGroup) {
      renderMarkers(L, mapInstanceRef.current);
    }
  }, [items, selected]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (map && items.length > 0 && !locationQuery) {
      const lats = items.map((i) => parseFloat(i.latitude)).filter(Boolean);
      const lngs = items.map((i) => parseFloat(i.longitude)).filter(Boolean);
      if (lats.length) {
        map.fitBounds(
          [
            [Math.min(...lats), Math.min(...lngs)],
            [Math.max(...lats), Math.max(...lngs)],
          ],
          { padding: [40, 40], maxZoom: 14 }
        );
      }
    }
  }, [items, locationQuery]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (map && locationQuery) {
      fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(locationQuery + ', Vietnam')}&format=geojson&polygon_geojson=1`)
        .then(res => res.json())
        .then(data => {
          if (data && data.features && data.features.length > 0) {
            const feature = data.features[0];
            const boundingbox = feature.properties ? feature.properties.boundingbox : feature.boundingbox;

            if (polygonRef.current) {
              polygonRef.current.remove();
              polygonRef.current = null;
            }

            if (feature.geometry && (feature.geometry.type === 'Polygon' || feature.geometry.type === 'MultiPolygon')) {
              const polygon = (window as any).L.geoJSON(feature, {
                style: {
                  color: '#f97316',
                  weight: 2,
                  opacity: 1,
                  fillColor: '#f97316',
                  fillOpacity: 0.15
                }
              }).addTo(map);
              polygonRef.current = polygon;
              map.fitBounds(polygon.getBounds(), { padding: [40, 40], maxZoom: 14 });
            } else if (boundingbox) {
              const bounds: any = [
                [parseFloat(boundingbox[0]), parseFloat(boundingbox[2])],
                [parseFloat(boundingbox[1]), parseFloat(boundingbox[3])]
              ];
              map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
            } else if (feature.geometry && feature.geometry.coordinates) {
              // Fallback to point
              map.flyTo([feature.geometry.coordinates[1], feature.geometry.coordinates[0]], 14);
            }
          }
        })
        .catch(err => console.error(err));
    } else if (map && !locationQuery) {
      // Clear polygon if no search query
      if (polygonRef.current) {
        polygonRef.current.remove();
        polygonRef.current = null;
      }
    }
  }, [locationQuery]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    const L = (window as any).L;
    if (!map || !L) return;

    const clearRoute = () => {
      if (routeLayerRef.current) {
        map.removeLayer(routeLayerRef.current);
        routeLayerRef.current = null;
      }
      routeLabelsRef.current.forEach(label => map.removeLayer(label));
      routeLabelsRef.current = [];
    };

    if (!showRoute || jitteredItems.length < 2) {
      clearRoute();
      return;
    }

    const fetchRoute = async () => {
      setRouteLoading(true);
      clearRoute();
      try {
        // OSRM accepts max 100 points. We slice if too many.
        const pointsToRoute = jitteredItems.slice(0, 50);
        const coordinates = pointsToRoute.map(item => `${item.renderLng},${item.renderLat}`).join(';');
        const url = `https://router.project-osrm.org/route/v1/driving/${coordinates}?overview=full&geometries=geojson&annotations=true`;

        const res = await fetch(url);
        const data = await res.json();

        if (data.code === 'Ok' && data.routes && data.routes.length > 0) {
          const route = data.routes[0];

          // Draw the route line
          const geoJsonLayer = L.geoJSON(route.geometry, {
            style: {
              color: '#3b82f6',
              weight: 6,
              opacity: 0.9
            }
          }).addTo(map);
          routeLayerRef.current = geoJsonLayer;

          // Process legs to show time and distance
          route.legs.forEach((leg: any, index: number) => {
            const startNode = pointsToRoute[index];
            const endNode = pointsToRoute[index + 1];

            // Calculate a midpoint for the label (simplistic average of start and end)
            const midLat = (startNode.renderLat + endNode.renderLat) / 2;
            const midLng = (startNode.renderLng + endNode.renderLng) / 2;

            const durationMins = Math.round(leg.duration / 60);
            const labelIcon = L.divIcon({
              className: '',
              html: `<div style="
                background: #3b82f6;
                color: white;
                padding: 4px 10px;
                border-radius: 12px;
                font-size: 12px;
                font-weight: 700;
                white-space: nowrap;
                box-shadow: 0 2px 4px rgba(0,0,0,0.3);
                position: relative;
              ">
                ${durationMins} phút
                <div style="position:absolute;bottom:-4px;left:50%;transform:translateX(-50%);border-width:5px 5px 0;border-style:solid;border-color:#3b82f6 transparent transparent transparent;"></div>
              </div>`,
              iconSize: null,
            });

            const marker = L.marker([midLat, midLng], { icon: labelIcon, zIndexOffset: 500 }).addTo(map);
            routeLabelsRef.current.push(marker);
          });

          // Adjust map bounds to fit route
          map.fitBounds(geoJsonLayer.getBounds(), { padding: [40, 40] });
        }
      } catch (err) {
        console.error("Failed to fetch route:", err);
      } finally {
        setRouteLoading(false);
      }
    };

    fetchRoute();
  }, [showRoute, jitteredItems]);

  return (
    <div className="absolute inset-0 w-full h-full rounded-xl overflow-hidden" style={{ minHeight: 400 }}>
      <div ref={mapRef} className="absolute inset-0 z-0" />

      {/* Route toggle button */}
      <div className="absolute top-4 right-4 z-[400]">
        <button
          onClick={() => setShowRoute(!showRoute)}
          disabled={jitteredItems.length < 2 || routeLoading}
          className={`px-4 py-2 rounded-lg font-medium shadow-md transition-all flex items-center gap-2 ${showRoute ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
        >
          {routeLoading ? (
            <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
          ) : (
            <span>📍</span>
          )}
          {showRoute ? 'Ẩn lộ trình' : 'Hiển thị lộ trình'}
        </button>
      </div>
    </div>
  );
}
